import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, Platform } from "@ionic/angular";
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LogicLog } from 'src/model/class/general classes/logic-log';
import { Notific } from 'src/model/class/general classes/notific';
import { NotificationRequest_ShowPopup } from 'src/model/class/general classes/notification-request';
import { StaticClass } from 'src/components/shared-components/class/general-classes/static-classes';
import { ConnectionType } from 'src/components/shared-components/enum/enumSettings';
import { GenericStoreApiData } from 'src/components/shared-components/interfaces/generic-store-api-data';
import { iConnToolbarColour } from 'src/components/shared-components/interfaces/iConnToolbarColour';
import { iEvent } from 'src/components/shared-components/interfaces/iEvent';
import { EventService } from 'src/service/event.service';
import { LogicsService } from 'src/service/logics.service';
import { NetworkService } from 'src/service/network.service';
import { NotificationService } from 'src/service/notification.service';
import { TranslationService } from 'src/service/translation.service';
import { listNotification } from 'src/state/selectors/notifications.selectors';

@Component({
    selector: 'app-grid-popup-alarm',
    templateUrl: './grid-popup-alarm.component.html',
    styleUrls: ['./grid-popup-alarm.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GridPopupAlarmComponent implements OnInit, OnDestroy, iEvent, iConnToolbarColour {
  @Input() alarmInfo: Subject<NotificationRequest_ShowPopup>;

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectListNotificationUserSub: Subscription = null;

  private _networkServiceSubscription: Subscription = null;
  private _alarmInfoSubscription: Subscription = null;
  private _notifications: Array<Notific> = null;
  private _allActiveAlarms: Array<LogicLog> = new Array<LogicLog>();
  private _activeAlarms: Array<LogicLog> = new Array<LogicLog>();
  private _maxAlarmsVisibleApp: number = 1;

  public lastActiveAlarms: Array<LogicLog> = new Array<LogicLog>();
  public totalActiveAlarms: number = 0;
  public cssClasses: string = "";
  public _eventSubscriptions: Array<Subscription> = null;
  public otherAlarmsPresent: boolean = false;
  public maxAlarmsVisible: number = 6;
  public isApp: boolean = false;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  constructor(
    public translationService: TranslationService,
    private _logicService: LogicsService,
    private _modalController: ModalController,
    private _eventService: EventService,
    private _store: Store,
    private _notificService: NotificationService,
    private _platform: Platform,
    private _networkService: NetworkService
  ) {
    this._notificService.Init();
    this.loadNotifications();
    this.isApp = this._platform.is("ios") || this._platform.is("android");
  }

  ngOnInit(): void {
    this.subscribeNetworkServiceData();
    if (this.isApp) {
      this.maxAlarmsVisible = this._maxAlarmsVisibleApp;
    }
    this.onClickOnIonBackdrop();
    this.subscribeEvents();
    let timer = setTimeout(() => {
      this.subscribeAlarmInfo();
      clearTimeout(timer);
    }, 100);
  }

  ngOnDestroy(): void {
    this._networkServiceSubscription?.unsubscribe();
    this._alarmInfoSubscription?.unsubscribe();
    this.unsubscribeEvents();
    this.unsubscribeStoreSelect();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalController.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * When a value arrives, you unsubscribe and subscribe
   * to alarmInfo in order to update the list of active alarms.   
   * @param value 
   */
  public solveReset(value: string) {
    this._alarmInfoSubscription?.unsubscribe();
    let timeout = setTimeout(() => {
      this.subscribeAlarmInfo();
      clearTimeout(timeout);
    }, 100);
  }

  /**
   * If the active alarms are all closable, it allows to close the ion-modal.
   */
  public onClickDismissModal() {
    let closable: boolean = true;
    let notific = this._notifications.find(notific => notific.id == this.lastActiveAlarms[0].notificationId);
    if (!notific.allowPopupClosure) {
      closable = false;
    }

    if (closable) {
      this._modalController.dismiss();
    }
  }

  /**
   * Subscribte to AlarmInfo
   */
  private subscribeAlarmInfo(): void {
    this._alarmInfoSubscription = this.alarmInfo.subscribe(async () => {
      const alarms = await this._logicService.GetActiveAlarms(true, 0);
      if (alarms) {
        if (alarms instanceof Array) {
          this._allActiveAlarms = alarms;
        } else {
          this._allActiveAlarms.push(alarms);
        }
        this.setAlarmList();
      } else {
        await this._modalController.dismiss();
      }
    });
  }

  /**
   * Set the list of main alarms.
   */
  private setAlarmList(): void {
    this.lastActiveAlarms = [];
    this._activeAlarms = [];
    for (let i = 0; i < this._allActiveAlarms.length; i++) {
      if (this._activeAlarms.findIndex((alarm: LogicLog) => alarm.idbigLogic === this._allActiveAlarms[i].idbigLogic) < 0) {
        let notific = this._notifications.find(notif => notif.id == this._allActiveAlarms[i].notificationId);
        if (notific.popupCustomTitle != "") {
          this._allActiveAlarms[i].description = notific.popupCustomTitle;
        }
        this._activeAlarms.push(this._allActiveAlarms[i]);
      }
    }
    this.otherAlarmsPresent = this._activeAlarms.length > 1;
    this.totalActiveAlarms = this._activeAlarms.length;
    this.lastActiveAlarms = this._activeAlarms.slice(0, this.maxAlarmsVisible);
    this.getCSSClasses();
  }

  /**
   * Set the css classes of the divs.
   */
  private getCSSClasses(): void {
    this.cssClasses = "";
    const cssOne = "notification-one";
    const cssTwo = "notification-two";
    const cssThree = "notification-three";
    const cssFour = "notification-four";
    const cssFive = "notification-five";
    const cssOverSix = "notification-over-six";
    const closableOneRow = "notification-one-two-three-closable";

    switch (this.lastActiveAlarms.length) {
      case 1:
        this.cssClasses = cssOne;
        break;
      case 2:
        if (this.totalActiveAlarms > this.maxAlarmsVisible) {
          this.cssClasses = cssTwo + " " + closableOneRow;
        } else {
          this.cssClasses = cssOne + " " + cssTwo;
        }
        break;
      case 3:
        if (this.totalActiveAlarms > this.maxAlarmsVisible) {
          this.cssClasses = cssTwo + " " + cssThree + " " + closableOneRow;
        } else {
          this.cssClasses = cssOne + " " + cssTwo + " " + cssThree;
        }
        break;
      case 4:
        this.cssClasses = cssTwo;
        if (this.totalActiveAlarms > this.maxAlarmsVisible) {
          this.cssClasses = this.cssClasses + " " + cssOverSix;
        } else {
          this.cssClasses = this.cssClasses + " " + cssFour;
        }
        break;
      case 5:
        this.cssClasses = cssTwo + " " + cssFive;
        if (this.totalActiveAlarms > this.maxAlarmsVisible) {
          this.cssClasses = this.cssClasses + " " + cssOverSix;
        } else {
          this.cssClasses = this.cssClasses + " " + cssFour;
        }
        break;
      default:
        this.cssClasses = cssTwo + " " + cssThree;
        if (this.totalActiveAlarms - this.lastActiveAlarms.length == 0) {
          this.cssClasses = this.cssClasses + " " + cssFour;
        } else {
          this.cssClasses = this.cssClasses + " " + cssOverSix;
        }
        break;
    };
  }

  /**
   * Adds an event listener to the ion-modal ion-backdrop
   * so that once clicked it hides all closable alarms.
   */
  private onClickOnIonBackdrop(): void {
    const modal = document.getElementsByTagName("ion-modal");
    const backdrop = modal[0].shadowRoot.querySelector('ion-backdrop');
    backdrop.stopPropagation = false;
    backdrop.addEventListener('click', () => {
      let notClosableAlarms: Array<LogicLog> = [];
      for (let i = 0; i < this._activeAlarms.length; i++) {
        let notific = this._notifications.find(notif => notif.id == this._activeAlarms[i].notificationId);
        if (!notific.allowPopupClosure) {
          notClosableAlarms.push(this._activeAlarms[i]);
        }
      }
      this.totalActiveAlarms = this.maxAlarmsVisible + this._activeAlarms.length - notClosableAlarms.length;
      this.lastActiveAlarms = notClosableAlarms.splice(0, this.maxAlarmsVisible);
      this.getCSSClasses();
      if (this.lastActiveAlarms.length == 0) {
        this._modalController.dismiss();
      }
    });
  }

  /**
   * Upload all notifications from the store.
   */
  private loadNotifications(): void {
    this._selectListNotificationUserSub = this._store
      .select(listNotification)
      .pipe(
        filter(
          (listNotificationObject: GenericStoreApiData) => listNotificationObject !== null && listNotificationObject !== undefined
        ),
        filter(
          (listNotificationObject: GenericStoreApiData) =>
            listNotificationObject.loaded
        )
      )
      .subscribe(async (listNotificationObject: GenericStoreApiData) => {
        this._notifications = (listNotificationObject.data?.data || []);
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectListNotificationUserSub.unsubscribe();
  }
  //#endregion

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
